/**
 * The shell: track switch, sidebar, page, and the gate in front of the developer
 * track.
 *
 * Routing is path-based (`/business/pricing`, `/developer/quickstart`) rather than
 * hash-based, because these URLs get pasted into tickets and a hash reads like an
 * anchor. `server.ts` serves the shell for every unknown path, so a deep link
 * boots the client instead of 404ing.
 *
 * The gate is not a route guard. Business pages are in this bundle; developer
 * pages are *fetched* after sign-in. Hiding a bundled page behind a client-side
 * check would put the whole developer track one `view-source` away.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { BlockView } from './blocks.js';
import { BUSINESS_PAGES } from './content/business.js';
import { fetchDeveloper, logout, session as fetchSession, type DeveloperPayload } from './api.js';
import { Gate } from './gate.js';
import type { Page, Session, Track } from './types.js';

// ── routing ──────────────────────────────────────────────────────────────────

interface Route {
  track: Track;
  slug: string;
}

function parse(pathname: string): Route {
  const parts = pathname.replace(/^\/+|\/+$/g, '').split('/');
  const track: Track = parts[0] === 'developer' ? 'developer' : 'business';
  return { track, slug: parts.slice(1).join('/') };
}

export const hrefFor = (track: Track, slug: string): string =>
  slug ? `/${track}/${slug}` : `/${track}`;

function useRoute(): [Route, (track: Track, slug: string) => void] {
  const [route, setRoute] = useState<Route>(() => parse(window.location.pathname));

  useEffect(() => {
    const onPop = (): void => setRoute(parse(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    // Intercept internal links once, at the document, rather than wiring an
    // onClick into every card and every inline link the renderer produces.
    const onClick = (event: MouseEvent): void => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
      const anchor = (event.target as HTMLElement | null)?.closest?.('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href') ?? '';
      if (!href.startsWith('/') || anchor.getAttribute('target')) return;
      event.preventDefault();
      window.history.pushState({}, '', href);
      setRoute(parse(href));
      window.scrollTo(0, 0);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const go = (track: Track, slug: string): void => {
    const href = hrefFor(track, slug);
    window.history.pushState({}, '', href);
    setRoute({ track, slug });
    window.scrollTo(0, 0);
  };

  return [route, go];
}

// ── shell ────────────────────────────────────────────────────────────────────

export default function App(): ReactNode {
  const [route, go] = useRoute();
  const [who, setWho] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [developer, setDeveloper] = useState<DeveloperPayload | null>(null);
  const [theme, setTheme] = useState<string>(
    () => document.documentElement.getAttribute('data-theme') ?? 'dark',
  );

  useEffect(() => {
    void fetchSession()
      .then(setWho)
      .catch(() => setWho({ signed_in: false }))
      .finally(() => setReady(true));
  }, []);

  // Developer content is fetched, never bundled — see the file header.
  useEffect(() => {
    if (!who?.signed_in || developer) return;
    void fetchDeveloper()
      .then(setDeveloper)
      .catch(() => setDeveloper(null));
  }, [who?.signed_in, developer]);

  const pages: Page[] =
    route.track === 'developer' ? (developer?.pages ?? []) : BUSINESS_PAGES;

  const page = useMemo(
    () => pages.find((p) => p.slug === route.slug) ?? pages.find((p) => p.slug === '') ?? null,
    [pages, route.slug],
  );

  const groups = useMemo(() => {
    const out = new Map<string, Page[]>();
    for (const p of pages) {
      const list = out.get(p.group) ?? [];
      list.push(p);
      out.set(p.group, list);
    }
    return [...out.entries()];
  }, [pages]);

  useEffect(() => {
    document.title = page ? `${page.title} · Nuqi docs` : 'Nuqi · Documentation';
  }, [page]);

  function toggleTheme(): void {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    try {
      localStorage.setItem('nuqi.docs.theme', next);
    } catch {
      /* private mode */
    }
    setTheme(next);
  }

  const gated = route.track === 'developer' && !who?.signed_in;

  return (
    <div className="app">
      <header className="topbar">
        <a className="brand" href="/business" style={{ color: 'inherit' }}>
          <span className="dot" />
          Nuqi <small>docs</small>
        </a>

        <div className="track" role="tablist">
          <button
            role="tab"
            aria-selected={route.track === 'business'}
            onClick={() => go('business', '')}
          >
            Business
          </button>
          <button
            role="tab"
            aria-selected={route.track === 'developer'}
            onClick={() => go('developer', '')}
          >
            Developer
            {!who?.signed_in ? <span className="lock">🔒</span> : null}
          </button>
        </div>

        <span className="spacer" />

        <button className="icon-btn" onClick={toggleTheme} title="Switch theme" aria-label="Switch theme">
          {theme === 'dark' ? '☀' : '☾'}
        </button>

        {who?.signed_in ? (
          <div className="who">
            <span className="av">{(who.display_name ?? '?').slice(0, 2).toUpperCase()}</span>
            <button
              className="icon-btn"
              title={`Sign out ${who.email ?? ''}`}
              aria-label="Sign out"
              onClick={() => {
                void logout().then(() => {
                  setWho({ signed_in: false });
                  setDeveloper(null);
                  go('business', '');
                });
              }}
            >
              ⏻
            </button>
          </div>
        ) : null}
      </header>

      {!ready ? (
        <div className="body">
          <div className="main">
            <div className="skeleton" style={{ width: '40%', height: 30 }} />
            <div className="skeleton" style={{ width: '70%' }} />
            <div className="skeleton" style={{ width: '55%' }} />
          </div>
        </div>
      ) : gated ? (
        <Gate
          onSignedIn={(next) => {
            setWho(next);
            go('developer', '');
          }}
        />
      ) : (
        <div className="body">
          <nav className="side">
            {groups.map(([group, list]) => (
              <div key={group}>
                <h5>{group}</h5>
                {list.map((p, i) => (
                  <a
                    key={p.slug}
                    href={hrefFor(route.track, p.slug)}
                    aria-current={p.slug === (page?.slug ?? '') ? 'page' : undefined}
                  >
                    <span className="n">{String(i + 1).padStart(2, '0')}</span>
                    {p.title}
                  </a>
                ))}
              </div>
            ))}
          </nav>

          <main className="main">
            {!page ? (
              <>
                <div className="skeleton" style={{ width: '40%', height: 30 }} />
                <div className="skeleton" style={{ width: '65%' }} />
              </>
            ) : (
              <>
                <div className="crumb">
                  {route.track} · {page.group}
                </div>
                <h1 className="page-h1">{page.title}</h1>
                <p className="page-lede">{page.lede}</p>
                {page.blocks.map((b, i) => (
                  <BlockView key={i} block={b} session={who} />
                ))}
              </>
            )}
          </main>
        </div>
      )}
    </div>
  );
}
